import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import ForceLight from './components/ForceLight';
import LazyRouteBoundary from './components/LazyRouteBoundary';
import AppErrorBoundary from './components/AppErrorBoundary';
import { adminEntry } from './lib/osEntry';
import { initTheme } from './lib/theme';
import { installPreloadRecovery, lazyWithReload } from './lib/lazyWithReload';
import './styles.css';

const Landing = lazyWithReload(() => import('./pages/Landing'));
const InviteLanding = lazyWithReload(() => import('./pages/InviteLanding'));
const AdminAuth = lazyWithReload(() => import('./pages/AdminAuth'));
const AdminDashboard = lazyWithReload(() => import('./pages/AdminDashboard'));
const WorkspaceEditCallsheet = lazyWithReload(() => import('./pages/WorkspaceEditCallsheet'));
const AdminDirectory = lazyWithReload(() => import('./pages/AdminDirectory'));
const AdminPolls = lazyWithReload(() => import('./pages/AdminPolls'));
const AdminPollDetail = lazyWithReload(() => import('./pages/AdminPollDetail'));
const AdminSecurity = lazyWithReload(() => import('./pages/AdminSecurity'));
const AdminSettings = lazyWithReload(() => import('./pages/AdminSettings'));
const PollResponseView = lazyWithReload(() => import('./pages/PollResponseView'));
const PublicCallsheet = lazyWithReload(() => import('./pages/PublicCallsheet'));
const ClientEditCallsheet = lazyWithReload(() => import('./pages/ClientEditCallsheet'));
const ReviewCallsheet = lazyWithReload(() => import('./pages/ReviewCallsheet'));
const ShootDayView = lazyWithReload(() => import('./pages/ShootDayView'));
const PrepView = lazyWithReload(() => import('./pages/PrepView'));
const WrapView = lazyWithReload(() => import('./pages/WrapView'));
const DayTracker = lazyWithReload(() => import('./pages/DayTracker'));
const DayFeedback = lazyWithReload(() => import('./pages/DayFeedback'));
const AdminGuard = lazyWithReload(() => import('./components/AdminGuard'));

installPreloadRecovery();
initTheme();

function AdminCallsheetRedirect() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={adminEntry(id ?? "", search)} replace />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
    <BrowserRouter>
      <LazyRouteBoundary>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<ForceLight><Landing /></ForceLight>} />
            <Route path="/admin/setup" element={<AdminAuth mode="setup" />} />
            <Route path="/admin/login" element={<AdminAuth mode="login" />} />
            <Route
              path="/admin"
              element={
                <AdminGuard>
                  <AdminDashboard />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/security"
              element={
                <AdminGuard>
                  <AdminSecurity />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminGuard>
                  <AdminSettings />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/directory"
              element={
                <AdminGuard>
                  <AdminDirectory />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/polls"
              element={
                <AdminGuard>
                  <AdminPolls />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/polls/:id"
              element={
                <AdminGuard>
                  <AdminPollDetail />
                </AdminGuard>
              }
            />
            <Route path="/poll/:token" element={<ForceLight><PollResponseView /></ForceLight>} />
            <Route path="/invite/:token" element={<ForceLight><InviteLanding /></ForceLight>} />
            <Route
              path="/admin/callsheets/:id"
              element={
                <AdminGuard>
                  <AdminCallsheetRedirect />
                </AdminGuard>
              }
            />
            {/* Workspace editor: one Build section per route. Admin-only, so it
                keeps the theme toggle (never ForceLight). The optional :sub
                segment carries Delivery's drill-ins (delivery/track, …/automate). */}
            <Route
              path="/admin/callsheets/:id/:section"
              element={
                <AdminGuard>
                  <WorkspaceEditCallsheet />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/callsheets/:id/:section/:sub"
              element={
                <AdminGuard>
                  <WorkspaceEditCallsheet />
                </AdminGuard>
              }
            />
            <Route path="/c/:slug" element={<ForceLight><PublicCallsheet /></ForceLight>} />
            <Route path="/edit/:slug" element={<ForceLight><ClientEditCallsheet /></ForceLight>} />
            <Route path="/review/:slug" element={<ForceLight><ReviewCallsheet /></ForceLight>} />
            <Route path="/shoot/:slug" element={<ForceLight><ShootDayView /></ForceLight>} />
            <Route path="/day/:slug" element={<ForceLight><DayTracker /></ForceLight>} />
            <Route
              path="/day-feedback/:slug/:mode"
              element={<ForceLight><DayFeedback /></ForceLight>}
            />
            <Route
              path="/prep/:slug"
              element={
                <AdminGuard>
                  <PrepView />
                </AdminGuard>
              }
            />
            <Route
              path="/wrap/:slug"
              element={
                <AdminGuard>
                  <WrapView />
                </AdminGuard>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </LazyRouteBoundary>
    </BrowserRouter>
    </AppErrorBoundary>
  </StrictMode>,
);
